import { supabase } from '../lib/supabase';

export const storageService = {
  // Upload catalog file (PDF or images) - Private bucket
  async uploadCatalogFile(file, companyId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('catalog-files')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;
      return { path: data?.path, fullPath: data?.fullPath };
    } catch (error) {
      console.error('Error uploading catalog file:', error?.message);
      throw error;
    }
  },

  // Get signed URL for private catalog file
  async getCatalogFileUrl(filePath, expiresIn = 3600) {
    try {
      if (!filePath) {
        throw new Error('File path is required to generate a signed URL');
      }

      const attemptSignedUrl = async () => {
        const { data, error } = await supabase?.storage
          ?.from('catalog-files')
          ?.createSignedUrl(filePath, expiresIn);

        return { data, error };
      };

      let lastError;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const { data, error } = await attemptSignedUrl();

        if (!error && data?.signedUrl) {
          return data?.signedUrl;
        }

        lastError = error;
        const status = error?.statusCode || error?.status;

        if (status === 502 && attempt === 0) {
          await new Promise(resolve => setTimeout(resolve, 300));
          continue;
        }
        break;
      }

      if (typeof window !== 'undefined' && window?.URL?.createObjectURL) {
        const { data: fileData, error: downloadError } = await supabase?.storage
          ?.from('catalog-files')
          ?.download(filePath);

        if (downloadError) throw downloadError;
        if (fileData) {
          return window.URL.createObjectURL(fileData);
        }
      }

      throw lastError;
    } catch (error) {
      console.error('Error getting catalog file URL:', error?.message);
      throw error;
    }
  },

  // Upload catalog thumbnail - Public bucket
  async uploadThumbnail(file) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;

      const { data, error } = await supabase?.storage?.from('catalog-thumbnails')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage?.from('catalog-thumbnails')?.getPublicUrl(data?.path);

      return { path: data?.path, publicUrl };
    } catch (error) {
      console.error('Error uploading thumbnail:', error?.message);
      throw error;
    }
  },

  // Upload catalog page image to PUBLIC bucket (for published catalogs)
  async uploadPublicCatalogImage(file, companyId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('catalog-images-public')?.upload(filePath, file, {
          cacheControl: '31536000', // 1 year cache for public images
          upsert: false
        });

      if (error) throw error;

      // Get public URL (no expiration, works cross-domain)
      const { data: { publicUrl } } = supabase?.storage?.from('catalog-images-public')?.getPublicUrl(data?.path);

      return { path: data?.path, publicUrl };
    } catch (error) {
      console.error('Error uploading public catalog image:', error?.message);
      throw error;
    }
  },

  // Upload original PDF directly to public bucket
  async uploadPublicPdf(file, companyId) {
    try {
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.pdf`;
      const filePath = `${companyId}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('catalog-images-public')?.upload(filePath, file, {
          cacheControl: '31536000',
          upsert: false,
          contentType: 'application/octet-stream'
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase?.storage?.from('catalog-images-public')?.getPublicUrl(data?.path);
      return { path: data?.path, publicUrl };
    } catch (error) {
      console.error('Error uploading PDF to public bucket:', error?.message);
      throw error;
    }
  },

  // Copy image from private bucket to public bucket (for publishing catalogs)
  async copyToPublicBucket(privateFilePath, companyId) {
    try {
      console.log('[COPY_TO_PUBLIC] Starting copy operation');
      console.log('[COPY_TO_PUBLIC] Source bucket: catalog-files');
      console.log('[COPY_TO_PUBLIC] Source path:', privateFilePath);
      console.log('[COPY_TO_PUBLIC] Target bucket: catalog-images-public');
      console.log('[COPY_TO_PUBLIC] Company ID:', companyId);

      // Check if source file exists first
      console.log('[COPY_TO_PUBLIC] Step 0: Verifying source file exists...');
      const fileExists = await this.checkFileExists('catalog-files', privateFilePath);
      
      if (!fileExists) {
        const error = new Error(`Source file not found in private bucket: ${privateFilePath}`);
        error.code = 'SOURCE_FILE_NOT_FOUND';
        console.error('[COPY_TO_PUBLIC] Source file does not exist');
        throw error;
      }

      // Download from private bucket
      console.log('[COPY_TO_PUBLIC] Step 1: Downloading from private bucket...');
      const { data: fileData, error: downloadError } = await supabase?.storage
        ?.from('catalog-files')
        ?.download(privateFilePath);

      if (downloadError) {
        console.error('[COPY_TO_PUBLIC] Download failed:', downloadError);
        console.error('[COPY_TO_PUBLIC] Error details:', {
          message: downloadError?.message,
          statusCode: downloadError?.statusCode,
          error: downloadError?.error
        });
        throw downloadError;
      }

      if (!fileData) {
        console.error('[COPY_TO_PUBLIC] No file data returned from download');
        throw new Error('No file data returned from private bucket');
      }

      console.log('[COPY_TO_PUBLIC] Download successful, file size:', fileData?.size);

      // Generate new filename for public bucket
      const fileExt = privateFilePath?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const publicFilePath = `${companyId}/${fileName}`;

      console.log('[COPY_TO_PUBLIC] Step 2: Uploading to public bucket...');
      console.log('[COPY_TO_PUBLIC] Target path:', publicFilePath);

      // Upload to public bucket
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('catalog-images-public')
        ?.upload(publicFilePath, fileData, {
          cacheControl: '31536000',
          upsert: false,
          contentType: fileData?.type || 'image/jpeg'
        });

      if (uploadError) {
        console.error('[COPY_TO_PUBLIC] Upload failed:', uploadError);
        console.error('[COPY_TO_PUBLIC] Error details:', {
          message: uploadError?.message,
          statusCode: uploadError?.statusCode,
          error: uploadError?.error
        });
        throw uploadError;
      }

      if (!uploadData?.path) {
        console.error('[COPY_TO_PUBLIC] No path returned from upload');
        throw new Error('Upload succeeded but no path returned');
      }

      console.log('[COPY_TO_PUBLIC] Upload successful, path:', uploadData?.path);

      // Get public URL
      console.log('[COPY_TO_PUBLIC] Step 3: Generating public URL...');
      const { data: { publicUrl } } = supabase?.storage
        ?.from('catalog-images-public')
        ?.getPublicUrl(uploadData?.path);

      console.log('[COPY_TO_PUBLIC] Public URL generated:', publicUrl);
      console.log('[COPY_TO_PUBLIC] Copy operation completed successfully');

      return { path: uploadData?.path, publicUrl };
    } catch (error) {
      console.error('[COPY_TO_PUBLIC] FATAL ERROR in copy operation:', error);
      console.error('[COPY_TO_PUBLIC] Error stack:', error?.stack);
      throw error;
    }
  },

  // Check if a file exists in a storage bucket
  async checkFileExists(bucketName, filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucketName)
        ?.list(filePath?.substring(0, filePath?.lastIndexOf('/')), {
          limit: 1000,
          search: filePath?.substring(filePath?.lastIndexOf('/') + 1)
        });

      if (error) {
        console.error(`[CHECK_FILE_EXISTS] Error checking file: ${filePath}`, error);
        return false;
      }

      const fileName = filePath?.substring(filePath?.lastIndexOf('/') + 1);
      const fileExists = data?.some(file => file?.name === fileName);
      
      console.log(`[CHECK_FILE_EXISTS] File ${filePath} exists: ${fileExists}`);
      return fileExists;
    } catch (error) {
      console.error('[CHECK_FILE_EXISTS] Error:', error);
      return false;
    }
  },

  // Get public URL for catalog image (no expiration, works in embed contexts)
  getPublicCatalogImageUrl(filePath) {
    try {
      if (!filePath) return null;
      
      const { data: { publicUrl } } = supabase?.storage
        ?.from('catalog-images-public')
        ?.getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error getting public catalog image URL:', error?.message);
      return null;
    }
  },

  // Copy PDF from private bucket to public bucket (for published catalogs)
  async copyPdfToPublicBucket(privatePdfPath, companyId) {
    try {
      console.log('[COPY_PDF_PUBLIC] Source path:', privatePdfPath);

      const fileExists = await this.checkFileExists('catalog-files', privatePdfPath);
      if (!fileExists) {
        const error = new Error(`Source PDF not found: ${privatePdfPath}`);
        error.code = 'SOURCE_FILE_NOT_FOUND';
        throw error;
      }

      const { data: fileData, error: downloadError } = await supabase?.storage
        ?.from('catalog-files')
        ?.download(privatePdfPath);

      if (downloadError) throw downloadError;
      if (!fileData) throw new Error('No file data returned from private bucket');

      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.pdf`;
      const publicFilePath = `${companyId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('catalog-images-public')
        ?.upload(publicFilePath, fileData, {
          cacheControl: '31536000',
          upsert: false,
          contentType: 'application/pdf'
        });

      if (uploadError) throw uploadError;
      if (!uploadData?.path) throw new Error('Upload succeeded but no path returned');

      const { data: { publicUrl } } = supabase?.storage
        ?.from('catalog-images-public')
        ?.getPublicUrl(uploadData?.path);

      console.log('[COPY_PDF_PUBLIC] PDF copied successfully:', publicUrl);
      return { path: uploadData?.path, publicUrl };
    } catch (error) {
      console.error('[COPY_PDF_PUBLIC] Error:', error);
      throw error;
    }
  },

  // Upload user avatar - Private bucket
  async uploadAvatar(file, userId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('avatars')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Overwrite existing avatar
        });

      if (error) throw error;
      return { path: data?.path };
    } catch (error) {
      console.error('Error uploading avatar:', error?.message);
      throw error;
    }
  },

  // Get signed URL for avatar
  async getAvatarUrl(filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage?.from('avatars')?.createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting avatar URL:', error?.message);
      throw error;
    }
  },

  // Delete file from storage
  async deleteFile(bucket, filePath) {
    try {
      const { error } = await supabase?.storage?.from(bucket)?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting file:', error?.message);
      throw error;
    }
  },

  // List files in a folder
  async listFiles(bucket, folder) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.list(folder, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' }
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error listing files:', error?.message);
      throw error;
    }
  }
};